import apiClient from './api'
import { API_CONFIG, STORAGE_KEYS } from '@/config/env'

export type GroupBy = 'day' | 'week'

export interface VolumeReportFilters {
  startDate: string
  endDate: string
  paybill?: string
  groupBy: GroupBy
}

export interface VolumeReportPoint {
  label: string
  deposits: number
  withdrawals: number
}

export interface ComparePeriodsRequest {
  left_period_start: string
  left_period_end: string
  right_period_start: string
  right_period_end: string
}

export interface TopTransaction {
  id: string
  msisdn: string
  amount: number
  paybill: string | null
  type: 'deposit' | 'withdrawal'
}

// Helper to convert date filters to backend format
const prepareParams = (filters: VolumeReportFilters) => {
  const params: Record<string, string> = {
    start: filters.startDate,
    end: filters.endDate,
    group_by: filters.groupBy,
  }
  if (filters.paybill) {
    params.paybill = filters.paybill
  }
  return params
}

const dateParamsOnly = (filters: VolumeReportFilters): Record<string, string> => ({
  start: filters.startDate,
  end: filters.endDate,
})

/** Browser download of CSV (uses same auth as apiClient). */
export async function downloadReportCsv(
  path: string,
  params: Record<string, string>,
  fileBase: string,
): Promise<void> {
  const token = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEYS.authToken) : null
  const base = `${API_CONFIG.baseUrl.replace(/\/$/, '')}/api/v1`
  const qs = new URLSearchParams({ ...params, format: 'csv' }).toString()
  const url = `${base}${path}?${qs}`
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  })
  if (!res.ok) {
    const t = await res.text()
    throw new Error(t || `Export failed (${res.status})`)
  }
  const blob = await res.blob()
  let name = `${fileBase}.csv`
  const cd = res.headers.get('Content-Disposition')
  if (cd) {
    const m = cd.match(/filename="([^"]+)"/)
    if (m) name = m[1]
  }
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = name
  a.click()
  URL.revokeObjectURL(a.href)
}

export interface StatusFunnelRow {
  status: string
  depositCount: number
  depositAmount: number
  directDepositCount: number
  directDepositAmount: number
  withdrawalCount: number
  withdrawalAmount: number
}

export interface SlaReport {
  deposits: { sampleSize: number; medianMinutes: number; p95Minutes: number; p99Minutes: number }
  withdrawals: { sampleSize: number; medianMinutes: number; p95Minutes: number; p99Minutes: number }
}

export interface FailureReasonRow {
  source: string
  reason: string
  count: number
}

export interface NetDailyRow {
  date: string
  deposits: number
  withdrawals: number
  net: number
}

export interface DepositSourceMixRow {
  date: string
  depositTableAmount: number
  directDepositAmount: number
  totalDepositAmount: number
}

export interface CohortAnalysisRow {
  cohort: string
  period: number
  users: number
  retention: number
  revenue: number
  transactions: number
}

export const reportService = {
  fetchVolumeReport: (filters: VolumeReportFilters) =>
    apiClient.get('/reports/volumes', { params: prepareParams(filters) }).then((res) => res.data),
  fetchChannelPerformance: (filters: VolumeReportFilters) =>
    apiClient.get('/reports/channel-performance', { params: prepareParams(filters) }).then((res) => res.data),
  fetchProcessingTimes: (filters: VolumeReportFilters) =>
    apiClient.get('/reports/processing-time', { params: prepareParams(filters) }).then((res) => res.data),
  fetchTopTransactions: (filters: VolumeReportFilters) =>
    apiClient.get<TopTransaction[]>('/reports/top-transactions', { params: prepareParams(filters) }).then((res) => res.data),
  comparePeriods: (params: ComparePeriodsRequest) =>
    apiClient.get('/reports/compare', { params }).then((res) => res.data),
  fetchStatusFunnel: (filters: VolumeReportFilters) =>
    apiClient.get<{ items: StatusFunnelRow[] }>('/reports/status-funnel', { params: dateParamsOnly(filters) }).then((r) => r.data),
  fetchSlaToCompleted: (filters: VolumeReportFilters) =>
    apiClient.get<SlaReport>('/reports/sla-to-completed', { params: dateParamsOnly(filters) }).then((r) => r.data),
  fetchFailureReasons: (filters: VolumeReportFilters) =>
    apiClient
      .get<{ items: FailureReasonRow[] }>('/reports/failure-reasons', { params: { ...dateParamsOnly(filters), limit: '40' } })
      .then((r) => r.data),
  fetchNetDaily: (filters: VolumeReportFilters) =>
    apiClient.get<{ items: NetDailyRow[] }>('/reports/net-daily', { params: dateParamsOnly(filters) }).then((r) => r.data),
  fetchDepositSourceMix: (filters: VolumeReportFilters) =>
    apiClient
      .get<{ items: DepositSourceMixRow[] }>('/reports/deposit-source-mix', { params: dateParamsOnly(filters) })
      .then((r) => r.data),
  fetchCohortAnalysis: (filters: VolumeReportFilters) =>
    apiClient.get<{ items: CohortAnalysisRow[] }>('/reports/cohort-analysis', { params: dateParamsOnly(filters) }).then((r) => r.data),
}


